import type React from 'react'
import { useState } from 'react'
import { cn } from '@/utils'
import { TooltipProvider } from '@/components/ui/tooltip'
import type { ShapeManagerRef } from '@/lib/shapes/types/shape-manager-ref'
import type { OnBatchShapesCommitted } from '@/lib/transforms/types/on-batch-shapes-committed'
import { TOOL_RAIL_ITEMS } from '../constants/tool-rail-items'
import { ColorEntry } from './color-entry'
import { PanelToggleButton } from './panel-toggle-button'
import { ToolRailButton } from './tool-rail-button'

interface ToolRailProps {
  className?: string
  activeItem: string
  isMobile: boolean
  isExpanded: boolean
  mobileOpen: boolean
  isNodeEditMode: boolean
  selectToolButtonRef: React.RefObject<HTMLButtonElement | null>
  typeToolButtonRef: React.RefObject<HTMLButtonElement | null>
  shapeManagerRef?: React.RefObject<ShapeManagerRef | null>
  onBatchShapesCommitted?: OnBatchShapesCommitted
  onTogglePanel: () => void
  onToolClick: (id: string) => void
}

/** The always-visible left icon rail: expand/collapse button plus one button per tool. */
export function ToolRail({
  className,
  activeItem,
  isMobile,
  isExpanded,
  mobileOpen,
  isNodeEditMode,
  selectToolButtonRef,
  typeToolButtonRef,
  shapeManagerRef,
  onBatchShapesCommitted,
  onTogglePanel,
  onToolClick,
}: ToolRailProps) {
  const [selectTooltipOpen, setSelectTooltipOpen] = useState(false)

  return (
    <TooltipProvider>
      <div
        className={cn(
          'w-12 bg-sidebar-background border-r border-border flex flex-col h-full max-h-screen z-50',
          className
        )}
      >
        {/* Expand/Open button */}
        <div className="flex flex-col gap-2 p-2">
          <PanelToggleButton
            isMobile={isMobile}
            isExpanded={isExpanded}
            mobileOpen={mobileOpen}
            onClick={onTogglePanel}
          />
        </div>

        {/* Icon Navigation */}
        <div className="flex flex-1 flex-col gap-2 p-2">
          {TOOL_RAIL_ITEMS.map((item) => {
            const isSelect = item.id === 'select'

            return (
              <ToolRailButton
                key={item.id}
                item={item}
                isActive={activeItem === item.id}
                showNodeEditIcon={isSelect && isNodeEditMode}
                buttonRef={
                  isSelect
                    ? selectToolButtonRef
                    : item.id === 'type'
                      ? typeToolButtonRef
                      : undefined
                }
                tooltipOpen={isSelect ? selectTooltipOpen : undefined}
                onTooltipOpenChange={
                  isSelect
                    ? (open) => {
                        if (!open) setSelectTooltipOpen(false)
                      }
                    : undefined
                }
                onPointerEnter={isSelect ? () => setSelectTooltipOpen(true) : undefined}
                onPointerLeave={isSelect ? () => setSelectTooltipOpen(false) : undefined}
                onClick={onToolClick}
              />
            )
          })}
          <ColorEntry
            shapeManagerRef={shapeManagerRef}
            onBatchShapesCommitted={onBatchShapesCommitted}
          />
        </div>
      </div>
    </TooltipProvider>
  )
}
